import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  AppearanceFields,
  type AppearanceValue,
} from "@/components/shell/BrandAppearanceFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError, api } from "@/lib/api";
import type { WorkspaceAccount } from "@/lib/account-workspace";

const accountSchema = z.object({
  name: z.string().trim().min(1, "请输入账户名称").max(100, "账户名称不能超过 100 个字符"),
});

type AccountFormValues = z.infer<typeof accountSchema>;

export type CreateAccountFormProps = {
  /** Reset form when this becomes true (e.g. dialog opened). */
  active?: boolean;
  idPrefix?: string;
  submitLabel?: string;
  onCancel?: () => void;
  onSuccess: (account: WorkspaceAccount) => void | Promise<void>;
};

export function CreateAccountForm({
  active = true,
  idPrefix = "create-account",
  submitLabel = "创建账户",
  onCancel,
  onSuccess,
}: CreateAccountFormProps) {
  const [appearance, setAppearance] = useState<AppearanceValue>({
    iconKey: null,
    color: null,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
    setError,
  } = useForm<AccountFormValues>({
    resolver: zodResolver(accountSchema),
    defaultValues: { name: "" },
  });

  const nameValue = watch("name");

  useEffect(() => {
    if (active) {
      reset({ name: "" });
      setAppearance({ iconKey: null, color: null });
    }
  }, [active, reset]);

  async function onSubmit({ name }: AccountFormValues) {
    try {
      const { account } = await api.fetch<{ account: WorkspaceAccount }>("/accounts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          icon_key: appearance.iconKey,
          color: appearance.color,
        }),
      });
      await onSuccess(account);
    } catch (caughtError) {
      setError("root", {
        message: caughtError instanceof ApiError ? caughtError.message : "创建账户失败，请稍后重试",
      });
    }
  }

  const nameId = `${idPrefix}-name`;

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="space-y-2">
        <Label htmlFor={nameId}>账户名称</Label>
        <Input id={nameId} autoFocus {...register("name")} aria-invalid={Boolean(errors.name)} />
        {errors.name ? (
          <p className="text-sm text-destructive" role="alert">
            {errors.name.message}
          </p>
        ) : null}
      </div>
      <AppearanceFields
        name={nameValue}
        value={appearance}
        onChange={setAppearance}
        emptyNameLabel="账户"
        description="默认用账户名首字；也可从 Lucide 精选图标中选择（可搜索）。"
        modal={Boolean(onCancel)}
        idPrefix={`${idPrefix}-appearance`}
      />
      {errors.root ? (
        <p
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {errors.root.message}
        </p>
      ) : null}
      <div className={`flex gap-2 ${onCancel ? "justify-end" : ""}`}>
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            取消
          </Button>
        ) : null}
        <Button type="submit" className={onCancel ? undefined : "w-full"} disabled={isSubmitting}>
          {isSubmitting ? "创建中…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}
